export const CAREER_OFFER_EVENTS = {
	PROMO_IMPRESSION: 'career_offer_promo_impression',
	PROMO_CLICK: 'career_offer_promo_click',
	SELECTOR_VIEW: 'career_offer_selector_view',
	LANGUAGE_SELECT: 'career_offer_language_select',
	AUTO_REDIRECT: 'career_offer_auto_redirect',
	LANDING_VIEW: 'career_offer_landing_view',
	SECTION_VIEW: 'career_offer_section_view',
	CTA_CLICK: 'career_offer_cta_click',
	SCROLL_DEPTH: 'career_offer_scroll_depth',
	ENGAGED_VISIT: 'career_offer_engaged_visit',
	DIALOG_OPEN: 'career_offer_dialog_open',
	DIALOG_CLOSE: 'career_offer_dialog_close',
	EXTERNAL_LINK_CLICK: 'career_offer_external_link_click',
	FORM_VIEW: 'career_offer_form_view',
	FORM_START: 'career_offer_form_start',
	FORM_SUBMIT_ATTEMPT: 'career_offer_form_submit_attempt',
	FORM_VALIDATION_ERROR: 'career_offer_form_validation_error',
	CAPTCHA_REQUESTED: 'career_offer_captcha_requested',
	CAPTCHA_PASSED: 'career_offer_captcha_passed',
	CAPTCHA_ERROR: 'career_offer_captcha_error',
	FORM_SUBMITTED: 'career_offer_form_submitted',
	FORM_DUPLICATE: 'career_offer_form_duplicate',
	FORM_SUBMIT_ERROR: 'career_offer_form_submit_error',
	UNAVAILABLE_VIEW: 'career_offer_unavailable_view',
	UNAVAILABLE_ACTION: 'career_offer_unavailable_action',
} as const;

export const CAREER_OFFER_GOALS = {
	BANNER_IMPRESSION: 'CAREER_OFFER_BANNER_IMPRESSION',
	BANNER_CLICK: 'CAREER_OFFER_BANNER_CLICK',
	BANNER_ARRIVAL: 'CAREER_OFFER_BANNER_ARRIVAL',
	APPLICATION_STARTED: 'CAREER_OFFER_APPLICATION_STARTED',
	APPLICATION_SUBMITTED: 'CAREER_OFFER_APPLICATION_SUBMITTED',
	QUESTION_STARTED: 'CAREER_OFFER_QUESTION_STARTED',
	QUESTION_SUBMITTED: 'CAREER_OFFER_QUESTION_SUBMITTED',
} as const;

export type CareerOfferEventName =
	(typeof CAREER_OFFER_EVENTS)[keyof typeof CAREER_OFFER_EVENTS];
export type CareerOfferGoal =
	(typeof CAREER_OFFER_GOALS)[keyof typeof CAREER_OFFER_GOALS];
export type CareerOfferTechnology = 'java' | 'go' | 'python' | 'other';
export type CareerOfferRouting = 'manual' | 'primary_language_auto' | 'direct';
export type CareerOfferVisitor =
	| 'anonymous'
	| 'authenticated'
	| 'authenticated_without_matching_language';
export type CareerOfferForm = 'application' | 'question';

export type CareerOfferEvent = {
	name: CareerOfferEventName;
	technology?: CareerOfferTechnology;
	placement?: string;
	campaign?: string;
	creative?: string;
	routing?: CareerOfferRouting;
	visitor?: CareerOfferVisitor;
	entrypoint?: string;
	source?: string;
	section?: string;
	form?: CareerOfferForm;
	result?: string;
	value?: string;
};

export type CareerOfferUtmParameters = {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_term?: string;
	utm_content?: string;
};

export type CareerOfferAttributionParameters = {
	first_touch?: CareerOfferUtmParameters;
	last_touch?: CareerOfferUtmParameters;
};

export type CareerOfferMetrica = {
	ymEvent: (
		...parameters: ['params', Record<string, unknown>] | ['hit', string]
	) => void;
	reachGoal: (target: string, parameters: Record<string, unknown>) => void;
};

const buildCareerOfferParameters = (
	event: CareerOfferEvent,
	attribution?: CareerOfferAttributionParameters,
) => ({
	career_offer: {
		...event,
		...(attribution && { attribution }),
	},
});

export function trackCareerOfferEvent(
	metrica: CareerOfferMetrica,
	event: CareerOfferEvent,
	attribution?: CareerOfferAttributionParameters,
) {
	try {
		metrica.ymEvent('params', buildCareerOfferParameters(event, attribution));
	} catch {
		// Analytics must never interrupt product behaviour.
	}
}

export function trackCareerOfferHit(metrica: CareerOfferMetrica, url: string) {
	try {
		metrica.ymEvent('hit', url);
	} catch {
		// Analytics must never interrupt product behaviour.
	}
}

export function trackCareerOfferGoal(
	metrica: CareerOfferMetrica,
	goal: CareerOfferGoal,
	event: CareerOfferEvent,
	attribution?: CareerOfferAttributionParameters,
) {
	try {
		metrica.reachGoal(goal, buildCareerOfferParameters(event, attribution));
	} catch {
		// Analytics must never interrupt product behaviour.
	}
}
